/**
 * Frontend Sentry bootstrap — errors + optional traces only.
 *
 * Session Replay is intentionally NOT installed (Clarity owns recording;
 * skipping Replay saves ~50 KB gz in the entry chunk).
 *
 * Side-effect: calling `initSentry()` at module load so this file can be
 * imported as the first internal import in main.tsx — before ./App pulls in
 * routes/index.tsx, which builds the router at module scope and needs
 * Sentry.init to have already run for wrapCreateBrowserRouter (sentry-javascript#16062).
 */

import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { getClaritySessionUrl, tagClarityWithSentryEventId } from './clarity';
import { scrubHeaders, scrubUrl } from './scrub';

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;

const IGNORE_ERRORS: Array<string | RegExp> = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  /^AbortError/,
  /Loading chunk [\d]+ failed/,
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
];

function readDsn(): string | undefined {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  return typeof dsn === 'string' && dsn.trim().length > 0 ? dsn.trim() : undefined;
}

function readEnvironment(): string {
  const env = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  if (typeof env === 'string' && env.trim().length > 0) return env.trim();
  return import.meta.env.PROD ? 'production' : 'development';
}

function readRelease(): string | undefined {
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  return typeof release === 'string' && release.trim().length > 0
    ? release.trim()
    : undefined;
}

function readTracesSampleRate(): number {
  const raw = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return DEFAULT_TRACES_SAMPLE_RATE;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1
    ? parsed
    : DEFAULT_TRACES_SAMPLE_RATE;
}

type ScrubbableEvent = {
  event_id?: string;
  tags?: Record<string, string>;
  request?: {
    url?: string;
    headers?: Record<string, string>;
  };
  breadcrumbs?: Array<{
    message?: string;
    data?: Record<string, unknown>;
  }>;
};

/**
 * Scrub credentials from outbound Sentry events. Exported for unit tests.
 */
export function scrubSentryEvent(event: ScrubbableEvent): ScrubbableEvent | null {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (event.request?.headers) {
    event.request.headers = scrubHeaders(event.request.headers);
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.data?.url === 'string') {
        crumb.data.url = scrubUrl(crumb.data.url);
      }
      if (typeof crumb.message === 'string' && crumb.message.includes('?')) {
        crumb.message = scrubUrl(crumb.message);
      }
    }
  }

  const clarityUrl = getClaritySessionUrl();
  if (clarityUrl) {
    event.tags = { ...event.tags, claritySessionUrl: clarityUrl };
  }
  if (event.event_id) {
    tagClarityWithSentryEventId(event.event_id);
  }

  return event;
}

let sentryInitialized = false;

/**
 * Initialize Sentry when a DSN is present and the build is production.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initSentry(): void {
  if (sentryInitialized) return;

  const dsn = readDsn();
  const enabled = import.meta.env.PROD && Boolean(dsn);
  if (!enabled || !dsn) {
    sentryInitialized = true;
    return;
  }

  Sentry.init({
    dsn,
    enabled: true,
    environment: readEnvironment(),
    release: readRelease(),
    sendDefaultPii: false,
    tracesSampleRate: readTracesSampleRate(),
    ignoreErrors: IGNORE_ERRORS,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    beforeSend(event) {
      return scrubSentryEvent(event as ScrubbableEvent) as typeof event;
    },
  });

  sentryInitialized = true;
}

export { Sentry };

// Side-effect init — must run when this module is first evaluated (main.tsx).
initSentry();
